/* eslint-disable react/no-array-index-key */
import { twJoin } from 'tailwind-merge';

import Icon from 'molecules/Icon';
import Image from 'molecules/Image';
import Link from 'molecules/Link';
import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';

import type { MeetTheSpeakersProps } from 'components/MeetTheSpeakers/config';
import type { FC } from 'react';

const MeetTheSpeakers: FC<MeetTheSpeakersProps> = ({ speakers = [], ...props }) => {
  const { sectionProps, rest } = splitSectionProps(props);
  const { heading, columns = 'three' } = rest;

  const alignment = heading?.alignment ?? 'center';
  const isCentered = alignment === 'center' || alignment === 'centerWide';

  // display-xs heading (not the Heading molecule) so it matches the EventOverview headings.
  const gridClass =
    columns === 'two'
      ? 'grid grid-cols-1 gap-20 pt-8 sm:grid-cols-2 sm:gap-10 sm:pt-12 lg:grid-cols-2 lg:gap-10 lg:pt-14'
      : 'grid grid-cols-1 gap-20 pt-8 sm:grid-cols-2 sm:gap-10 sm:pt-12 lg:grid-cols-3 lg:gap-10 lg:pt-14';

  return (
    <Section {...sectionProps} noDarkMode>
      {heading?.heading && (
        <div className={twJoin('flex flex-col gap-3', isCentered && 'mx-auto max-w-[960px] items-center text-center')}>
          {heading.eyebrow && (
            <span className="font-spaceGrotesk font-semibold uppercase text-eyebrow text-overline-sm">
              {heading.eyebrow}
            </span>
          )}
          <h2 className="font-spaceGrotesk text-display-xs font-semibold text-gray-900">{heading.heading}</h2>
        </div>
      )}
      <div className={gridClass}>
        {speakers.map((spk, i) => (
          <div key={i} className="flex gap-6">
            {spk?.photo?.file && (
              <Image
                {...spk.photo.file}
                alt={spk.photo.alt || spk.name || 'Speaker headshot'}
                url={spk.photo.file.url}
                noFill
                width={300}
                height={300}
                objectCover
                className="aspect-square size-[150px] shrink-0 rounded-lg"
                borderRadius="7px"
              />
            )}

            <div className="flex min-w-0 flex-col justify-center gap-3">
              {spk?.name && <div className="text-md font-semibold leading-6 text-gray-900">{spk.name}</div>}
              {spk?.title && <div className="text-sm leading-5 text-gray-700">{spk.title}</div>}

              {spk?.linkedinUrl && (
                <Link
                  href={spk.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${spk.name || 'Speaker'} on LinkedIn`}
                  className="inline-flex size-6 items-center justify-center text-gray-900"
                >
                  <Icon icon="linkedin-square" size={24} />
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};

export default MeetTheSpeakers;
